import React, {useCallback, useEffect, useMemo, useState} from "react";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import HomeIcon from "@mui/icons-material/Home";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import {Box, Button, Divider, Drawer, Grid, IconButton, Stack, Tooltip} from "@mui/material";
import {useTranslation} from "react-i18next";
import {useSelector} from "react-redux";
import {useNavigate} from "react-router-dom";
import {getCustomPageInternalUrl, getHomeInternalUrl} from "../../constants/appRoutes";
import {NodeVisibility} from "../../model/IHubMinimalNode.d.ts";
import {ConfigNodeMenuId} from "../../model/item-containers-models/configNodeMenuId";
import {I18N_ADORNMENT_VIEW_MODE_ICONTEXT} from "../i18n-input-adornment-select";
import ModulesPlaceholder from "../modules-placeholder";
import A11ySelectButton from "./components/A11ySelectButton";
import AppLanguageSelectButton from "./components/AppLanguageSelectButton";
import CategoriesButton from "./components/CategoriesButton";
import DashboardButton from "./components/DashboardButton";
import HubLogo from "./components/HubLogo";
import HubName from "./components/HubName";
import InfoButton from "./components/InfoButton";
import NavigationDrawerButtons from "./components/NavigationDrawerButtons";
import NodeHomeButton from "./components/NodeHomeButton";
import NodeSelector from "./components/NodeSelector";
import SearchButton from "./components/SearchButton";
import SettingsSelectButton from "./components/SettingsSelectButton";
import ToolsSelectButton from "./components/ToolsSelectButton";
import UserItemsSelectButton from "./components/UserItemsSettingsButton";
import UserSelectButton from "./components/UserSelectButton";
import {nodeCustomPagesSelector} from "../../state/node/nodeSelectors";
import {localizeI18nObj} from "../../utils/i18n";
import {getCustomPagesInMenu} from "../../utils/itemContainers";
import {areElementsOverlap} from "../../utils/other";
import {HEADER_BUTTON_VIEW_MODE_ICONTEXT, HEADER_BUTTON_VIEW_MODE_TEXT} from "./utils";

const firstRowContainerStyle = {
  position: "absolute",
  padding: "0 8px"
};

const firstRowLeftContainerStyle = {
  left: "0px",
  zIndex: 1
};

const firstRowRightContainerStyle = {
  right: "0px"
};

const hiddenContainerStyle = {
  visibility: "hidden"
};

const DrawerSection = ({children}) => (
  <Stack
    spacing={1}
    sx={{
      "& > .MuiGrid-root:first-of-type": {
        paddingTop: "8px",
        borderTop: "1px solid rgba(0, 0, 0, 0.12)"
      },
      "& > .MuiGrid-root:last-of-type": {
        paddingBottom: "8px"
      }
    }}
  >
    {children}
  </Stack>
);

const HeaderVariantDrawer = ({
  baseURL,
  defaultLanguage,
  languages,
  moduleConfigs,
  hub,
  nodes,
  node,
  title = node?.name,
  noNode = !node?.nodeId,
  catalog,
  user,
  filteredCatalog,
  emptyHeader,
  isDefaultAndUniqueNode,
  selectedCategoryPath,
  selectedDataset,
  onRender
}) => {
  const {t} = useTranslation();
  const navigate = useNavigate();

  const [isToolbarDrawerOpen, setisToolbarDrawerOpen] = useState(false);
  const [isFirstRowCollapsed, setisFirstRowCollapsed] = useState(false);
  const [isCutomPagesInSidenav, setIsCutomPagesInSidenav] = useState(false);

  const headerLogoSmallURL = useMemo(() => hub.extras.HeaderLogoSmallURL || "", [hub.extras]);
  const headerLogoAlt = useMemo(
    () => localizeI18nObj(hub.extras.HeaderLogoAlt, defaultLanguage, languages) || "",
    [hub.extras, defaultLanguage, languages]
  );
  const headerLogoHref = useMemo(() => hub.extras.HeaderLogoHref || "", [hub.extras]);
  const headerLogoTitle = useMemo(
    () => localizeI18nObj(hub.extras.headerLogoTitle, defaultLanguage, languages) || "",
    [hub.extras, defaultLanguage, languages]
  );

  const handleHeaderStyle = useCallback(() => {
    const customPagesOverlap = areElementsOverlap(
      "header__first-row__left-container",
      "header__first-row__right-container"
    );
    const appBarOverlap = areElementsOverlap(
      "header__first-row__left-container",
      "header__first-row__right-container__fixed-actions"
    );

    setIsCutomPagesInSidenav(customPagesOverlap);
    setisFirstRowCollapsed(appBarOverlap);

    if (onRender) {
      setTimeout(() => onRender(), 0);
    }
  }, [onRender]);

  useEffect(() => {
    window.addEventListener("resize", handleHeaderStyle);
    return () => {
      window.removeEventListener("resize", handleHeaderStyle);
    };
  }, [handleHeaderStyle]);

  useEffect(() => {
    handleHeaderStyle();
  });

  const onToolbarDrawerOpen = useCallback(() => {
    setisToolbarDrawerOpen(true);
  }, []);

  const onToolbarDrawerClose = useCallback(() => {
    setisToolbarDrawerOpen(false);
  }, []);

  const isSecondRowVisible = isFirstRowCollapsed && !noNode && !isDefaultAndUniqueNode;

  const nodeCustomPages = useSelector(nodeCustomPagesSelector);
  const customPagesInAppbar = getCustomPagesInMenu(nodeCustomPages, ConfigNodeMenuId.APPBAR);

  return (
    <>
      {emptyHeader ? (
        <Grid container columnSpacing={1} alignItems="center" wrap="nowrap" justifyContent="space-between">
          <HubLogo
            hub={hub}
            headerLogoTitle={headerLogoTitle}
            headerLogoHref={headerLogoHref}
            baseURL={baseURL}
            headerLogoAlt={headerLogoAlt}
            handleStyle={handleHeaderStyle}
            headerLogoSmallURL={headerLogoSmallURL}
          />
          <HubName hub={hub} />
        </Grid>
      ) : (
        <>
          <Box
            id="header__first-row"
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              minHeight: "64px"
            }}
          >
            <Box id="header__first-row__left-container" sx={[firstRowContainerStyle, firstRowLeftContainerStyle]}>
              <Grid container spacing={1} alignItems="center" wrap="nowrap">
                <>
                  {node?.visible !== NodeVisibility.No ? (
                    !noNode ? (
                      <>
                        {catalog && !catalog.isEmpty && (
                          <NavigationDrawerButtons
                            selectedCategoryPath={selectedCategoryPath}
                            selectedDataset={selectedDataset}
                            title={title}
                            nodes={nodes}
                            catalog={catalog}
                            node={node}
                            hub={hub}
                            filteredCatalog={filteredCatalog}
                            defaultLanguage={defaultLanguage}
                            languages={languages}
                            moduleConfigs={moduleConfigs}
                            additionalCustomPages={isCutomPagesInSidenav && customPagesInAppbar}
                          />
                        )}
                        <HubLogo
                          hub={hub}
                          headerLogoTitle={headerLogoTitle}
                          headerLogoHref={headerLogoHref}
                          baseURL={baseURL}
                          headerLogoAlt={headerLogoAlt}
                          handleStyle={handleHeaderStyle}
                          headerLogoSmallURL={headerLogoSmallURL}
                        />
                        <Grid container sx={[isFirstRowCollapsed && hiddenContainerStyle]}>
                          <NodeSelector isDefaultUniqueNode={isDefaultAndUniqueNode} node={node} nodes={nodes} />
                        </Grid>
                      </>
                    ) : (
                      <>
                        <HubLogo
                          hub={hub}
                          headerLogoTitle={headerLogoTitle}
                          headerLogoHref={headerLogoHref}
                          baseURL={baseURL}
                          headerLogoAlt={headerLogoAlt}
                          handleStyle={handleHeaderStyle}
                          headerLogoSmallURL={headerLogoSmallURL}
                        />
                        <HubName hub={hub} />
                      </>
                    )
                  ) : (
                    <Grid className="home-btn">
                      <Tooltip title={t("components.header.actions.home.title")}>
                        <IconButton
                          color="inherit"
                          onClick={() => navigate(getHomeInternalUrl())}
                          aria-label={t("components.header.actions.home.ariaLabel")}
                        >
                          <HomeIcon />
                        </IconButton>
                      </Tooltip>
                    </Grid>
                  )}
                </>
              </Grid>
            </Box>

            <Box id="header__first-row__right-container" sx={[firstRowContainerStyle, firstRowRightContainerStyle]}>
              <Grid container spacing={1} alignItems="center" wrap="nowrap">
                {!noNode && (
                  <Grid
                    id="header__first-row__right-container__dynamic-actions"
                    container
                    spacing={1}
                    alignItems="center"
                    wrap="nowrap"
                    sx={[
                      isCutomPagesInSidenav ? hiddenContainerStyle : {},
                      {
                        "& button:hover": {
                          background: theme => theme.palette.primary.main,
                          textDecoration: "underline",
                          textDecorationColor: theme => theme.palette.primary.contrastText,
                          textUnderlineOffset: "6px",
                          textDecorationThickness: "2px"
                        }
                      }
                    ]}
                  >
                    <NodeHomeButton viewMode={HEADER_BUTTON_VIEW_MODE_TEXT} />
                    <ModulesPlaceholder id="header__news-button" />
                    <CategoriesButton />
                    <ModulesPlaceholder id="header__navigation-buttons" />
                    <DashboardButton
                      moduleConfigs={moduleConfigs}
                      node={node}
                      hub={hub}
                      viewMode={HEADER_BUTTON_VIEW_MODE_TEXT}
                    />
                    {moduleConfigs.modules.includes("custom-page") &&
                      (customPagesInAppbar || []).map(page => (
                        <Grid key={page.id}>
                          <Button
                            id={`customPages-btn-${page.id}`}
                            sx={{color: theme => theme.palette.primary.contrastText}}
                            onClick={() => navigate(getCustomPageInternalUrl(page.id, node?.code))}
                          >
                            {localizeI18nObj(page.title, defaultLanguage, languages)}
                          </Button>
                        </Grid>
                      ))}
                    <Divider
                      orientation="vertical"
                      flexItem
                      sx={{
                        borderColor: "white",
                        borderWidth: "1px"
                      }}
                    />
                  </Grid>
                )}
                <Grid
                  id={"header__first-row__right-container__fixed-actions"}
                  container
                  spacing={0}
                  alignItems="center"
                  wrap="nowrap"
                >
                  <SearchButton noNode={noNode} catalog={catalog} />
                  <Grid className="toolbar-drawer-btn">
                    <Tooltip title={t("components.header.actions.openToolbarDrawer.title")}>
                      <IconButton
                        color="inherit"
                        onClick={onToolbarDrawerOpen}
                        aria-label={t("components.header.actions.openToolbarDrawer.ariaLabel")}
                      >
                        <MoreVertIcon />
                      </IconButton>
                    </Tooltip>
                  </Grid>
                </Grid>
              </Grid>
            </Box>
          </Box>

          {isSecondRowVisible && (
            <Box id="header__second-row" sx={{paddingLeft: "12px"}}>
              <NodeSelector isDefaultUniqueNode={isDefaultAndUniqueNode} node={node} nodes={nodes} />
            </Box>
          )}

          <Drawer
            anchor="right"
            open={isToolbarDrawerOpen}
            PaperProps={{
              sx: {
                overflow: "hidden",
                padding: "8px"
              }
            }}
            onClose={onToolbarDrawerClose}
          >
            <Box
              component="nav"
              id="toolbar-drawer"
              aria-label={t("components.header.toolbarDrawer.ariaLabel")}
              sx={{
                height: "100%"
              }}
            >
              <Stack
                sx={{
                  height: "100%",
                  "& .MuiGrid-root": {
                    width: "100%"
                  },
                  "& .MuiGrid-root button:not(.toolbar-drawer__collapse-btn)": {
                    width: "100%",
                    justifyContent: "flex-start"
                  },
                  "& .button-select__menu-button__end-icon": {
                    marginLeft: "auto",
                    paddingLeft: "8px"
                  },
                  "& button": {
                    color: theme => theme.palette.text.primary
                  }
                }}
              >
                <Grid>
                  <Tooltip title={t("components.header.actions.closeToolbarDrawer.title")}>
                    <IconButton
                      className="toolbar-drawer__collapse-btn"
                      onClick={onToolbarDrawerClose}
                      aria-label={t("components.header.actions.closeToolbarDrawer.ariaLabel")}
                    >
                      <ChevronRightIcon />
                    </IconButton>
                  </Tooltip>
                </Grid>
                <DrawerSection>
                  <UserSelectButton
                    hub={hub}
                    nodes={nodes}
                    user={user}
                    viewMode={HEADER_BUTTON_VIEW_MODE_ICONTEXT}
                    onNavigate={onToolbarDrawerClose}
                  />
                </DrawerSection>
                <DrawerSection>
                  <AppLanguageSelectButton
                    viewMode={I18N_ADORNMENT_VIEW_MODE_ICONTEXT}
                    onNavigate={onToolbarDrawerClose}
                  />
                  <A11ySelectButton viewMode={HEADER_BUTTON_VIEW_MODE_ICONTEXT} onNavigate={onToolbarDrawerClose} />
                </DrawerSection>
                <DrawerSection>
                  <UserItemsSelectButton
                    nodes={nodes}
                    user={user}
                    viewMode={HEADER_BUTTON_VIEW_MODE_ICONTEXT}
                    onNavigate={onToolbarDrawerClose}
                  />
                  <SettingsSelectButton
                    user={user}
                    nodes={nodes}
                    viewMode={HEADER_BUTTON_VIEW_MODE_ICONTEXT}
                    onNavigate={onToolbarDrawerClose}
                  />
                  <ToolsSelectButton
                    node={node}
                    user={user}
                    viewMode={HEADER_BUTTON_VIEW_MODE_ICONTEXT}
                    onNavigate={onToolbarDrawerClose}
                  />
                </DrawerSection>
                <Grid sx={{marginTop: "auto !important", marginBottom: "8px !important"}}>
                  <Divider />
                </Grid>
                <InfoButton hub={hub} viewMode={HEADER_BUTTON_VIEW_MODE_ICONTEXT} onNavigate={onToolbarDrawerClose} />
              </Stack>
            </Box>
          </Drawer>
        </>
      )}
      <Grid container className="header__bottom-border">
        <Grid size={4} className="header__bottom-border__left" />
        <Grid size={4} className="header__bottom-border__center" />
        <Grid size={4} className="header__bottom-border__right" />
      </Grid>
    </>
  );
};

export default HeaderVariantDrawer;
